import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, getDoc, onSnapshot, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { canAddVehicle, canAddDevice } from '../utils/permissions';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaBus,
  FaTools,
  FaUserFriends,
  FaCalendarAlt,
  FaTachometerAlt,
  FaIdCard,
  FaCogs,
  FaCalendarCheck,
  FaCalendar,
  FaNetworkWired,
} from 'react-icons/fa';
import MaintenanceModal from './MaintenanceModal';
import DriverAssignmentModal from './DriverAssignmentModal';
import DeviceAssignmentModal from './DeviceAssignmentModal';

const getTextColorForBg = (hexColor) => {
  if (!hexColor) return 'text-gray-800';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
  return luminance > 150 ? 'text-gray-800' : 'text-white';
};

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(204, 204, 204, ${alpha})`; // default gray
  const hexVal = hex.replace('#', '');
  const r = parseInt(hexVal.substring(0, 2), 16);
  const g = parseInt(hexVal.substring(2, 4), 16);
  const b = parseInt(hexVal.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Fleet = () => {
  const [vehicles, setVehicles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showSchedulesModal, setShowSchedulesModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterIgnition, setFilterIgnition] = useState('all');
  const [filterDeviceConnected, setFilterDeviceConnected] = useState('all');
  const [sortField] = useState('registrationNumber');
  const [sortDirection] = useState('asc');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingLogbook, setUploadingLogbook] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [deviceData, setDeviceData] = useState({}); // Store device data for linked devices
  const [busSchedules, setBusSchedules] = useState({});
  const [maintenanceSchedules, setMaintenanceSchedules] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDailyView, setShowDailyView] = useState(false);
  const [popoverGroupKey, setPopoverGroupKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [userRole, setUserRole] = useState(null);
  const [canAdd, setCanAdd] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [canAssignDevice, setCanAssignDevice] = useState(false);
  const [roleDetermined, setRoleDetermined] = useState(false); // Track if we've attempted to determine role

  // Form states
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    chassis: '',
    color: '',
    registrationNumber: '',
    yearOfManufacture: '',
    dateBought: '',
    mileage: '',
    cc: '',
    status: 'active',
    image: null,
    logbook: null,
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: '',
    insuranceDocument: null,
    seatCount: '',
    fleetNumber: '',
  });

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        // First, try to get user document by document ID (matching auth UID)
        let userDoc = await getDoc(doc(db, 'users', user.uid));
        
        // If document doesn't exist with that ID, try querying by uid field
        if (!userDoc.exists()) {
          const usersRef = collection(db, 'users');
          const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
          const querySnapshot = await getDocs(uidQuery);
          
          if (!querySnapshot.empty) {
            // Found user document with uid field matching auth UID
            userDoc = querySnapshot.docs[0];
          }
        }
        
        if (userDoc.exists()) {
          const role = userDoc.data().role;
          setUserRole(role);
          setCanAdd(canAddVehicle(role));
          setCanAssignDevice(canAddDevice(role));
          setCurrentUserId(user.uid);
        }
        // Mark role as determined (even if not found, we've checked)
        setRoleDetermined(true);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setRoleDetermined(true); // Mark as determined even on error
      }
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    // Fetch user profile pictures for all assigned drivers/assistants
    const fetchUserProfiles = async () => {
      const userIds = new Set();
      vehicles.forEach(vehicle => {
        if (vehicle.assignedDriver && vehicle.assignedDriver.id) userIds.add(vehicle.assignedDriver.id);
        if (vehicle.assignedAssistant && vehicle.assignedAssistant.id) userIds.add(vehicle.assignedAssistant.id);
      });
      const profiles = {};
      await Promise.all(Array.from(userIds).map(async (id) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', id));
          if (userDoc.exists()) {
            profiles[id] = userDoc.data();
          }
        } catch {}
      }));
      setUserProfiles(profiles);
    };
    if (vehicles.length > 0) fetchUserProfiles();
  }, [vehicles]);

  useEffect(() => {
    // Set up real-time listeners for device data from the 'devices' collection in Firestore
    // This reads directly from the same collection that Devices.js displays and that the API service updates
    const deviceIds = new Set();
    vehicles.forEach(vehicle => {
      if (vehicle.assignedDevice && vehicle.assignedDevice.id) {
        deviceIds.add(vehicle.assignedDevice.id);
      }
    });
    
    if (deviceIds.size === 0) {
      setDeviceData({});
      return;
    }


    // Normalize device data helper
    const normalizeDeviceData = (deviceData) => {
      // Normalize isIgnitionOn - handle both boolean and string values
      let isIgnitionOn = false;
      if (deviceData.isIgnitionOn !== undefined && deviceData.isIgnitionOn !== null) {
        if (typeof deviceData.isIgnitionOn === 'boolean') {
          isIgnitionOn = deviceData.isIgnitionOn;
        } else if (typeof deviceData.isIgnitionOn === 'string') {
          isIgnitionOn = deviceData.isIgnitionOn.toLowerCase() === 'true' || deviceData.isIgnitionOn === 'True';
        }
      }
      
      return {
        ...deviceData,
        vehicleSpeed: parseFloat(deviceData.vehicleSpeed) || 0,
        isIgnitionOn: isIgnitionOn,
        location: deviceData.location || 'Unknown',
        battery: parseFloat(deviceData.battery) || 0,
        odometer: parseFloat(deviceData.odometer) || 0,
        lastUpdated: deviceData.lastUpdated || deviceData.gpsDateTime || new Date().toISOString()
      };
    };

    // Set up real-time listeners for each device from the 'devices' collection
    const unsubscribeFunctions = Array.from(deviceIds).map(deviceId => {
      // Read from Firestore 'devices' collection using the device document ID
      const deviceRef = doc(db, 'devices', deviceId);
      
      // Set up real-time listener - updates automatically when device data changes in Firestore
      const unsubscribe = onSnapshot(
        deviceRef,
        (deviceDoc) => {
          if (deviceDoc.exists()) {
            const deviceData = deviceDoc.data();
            const normalizedData = normalizeDeviceData(deviceData);
            
          
            // Update device data state
            setDeviceData(prev => ({
              ...prev,
              [deviceId]: normalizedData
            }));
          } else {
            console.warn(`[Fleet] Device document ${deviceId} not found in Firestore 'devices' collection`);
            // Device was deleted, remove from state
            setDeviceData(prev => {
              const updated = { ...prev };
              delete updated[deviceId];
              return updated;
            });
          }
        },
        (error) => {
          console.error(`[Fleet] Error listening to device ${deviceId} from Firestore 'devices' collection:`, error);
        }
      );
      
      return unsubscribe;
    });

    // Cleanup: unsubscribe from all listeners when component unmounts or vehicles change
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [vehicles]);

  const syncMileageFromDevices = useCallback(async (vehiclesList) => {
    try {
      const vehiclesWithDevices = vehiclesList.filter(v => v.assignedDevice && v.assignedDevice.id);
      
      if (vehiclesWithDevices.length === 0) {
        return; // No vehicles with linked devices
      }

      // Fetch all devices in one query
      const devicesRef = collection(db, 'devices');
      const devicesSnapshot = await getDocs(devicesRef);
      const devicesMap = {};
      devicesSnapshot.docs.forEach(doc => {
        devicesMap[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Update mileage for vehicles with linked devices
      const updatePromises = vehiclesWithDevices.map(async (vehicle) => {
        const device = devicesMap[vehicle.assignedDevice.id];
        if (device && device.odometer !== undefined && device.odometer !== null) {
          const deviceOdometer = parseFloat(device.odometer) || 0;
          const currentMileage = parseFloat(vehicle.mileage) || 0;
          
          // Only update if device odometer is different (to avoid unnecessary writes)
          if (Math.abs(deviceOdometer - currentMileage) > 0.1) {
            try {
              const vehicleRef = doc(db, 'vehicles', vehicle.id);
              await updateDoc(vehicleRef, {
                mileage: deviceOdometer.toString(),
                lastMileageSync: new Date().toISOString()
              });
            } catch (updateError) {
              console.error(`Error updating mileage for vehicle ${vehicle.id}:`, updateError);
            }
          }
        }
      });

      await Promise.all(updatePromises);
      
      // Refresh vehicles after sync to show updated mileage
      const refreshedSnapshot = await getDocs(query(collection(db, 'vehicles'), orderBy(sortField, sortDirection)));
      let refreshedVehicles = refreshedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter vehicles for drivers/assistants
      if (userRole === 'driver' || userRole === 'assistant') {
        refreshedVehicles = refreshedVehicles.filter(vehicle => {
          return (vehicle.assignedDriver && vehicle.assignedDriver.id === currentUserId) ||
                 (vehicle.assignedAssistant && vehicle.assignedAssistant.id === currentUserId);
        });
      }
      
      setVehicles(refreshedVehicles);
    } catch (error) {
      console.error('Error syncing mileage from devices:', error);
      // Don't show error to user, just log it
    }
  }, [sortField, sortDirection, userRole, currentUserId]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Debug logging
      
      const vehiclesRef = collection(db, 'vehicles');
      const q = query(vehiclesRef, orderBy(sortField, sortDirection));
      const snapshot = await getDocs(q);
      let vehiclesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      
      // Filter vehicles for drivers/assistants - only show their assigned buses
      if (userRole === 'driver' || userRole === 'assistant') {
        if (currentUserId) {
          vehiclesList = vehiclesList.filter(vehicle => {
            const isAssigned = (vehicle.assignedDriver && vehicle.assignedDriver.id === currentUserId) ||
                               (vehicle.assignedAssistant && vehicle.assignedAssistant.id === currentUserId);
            return isAssigned;
          });
        } else {
          // If driver/assistant but currentUserId not set, show no vehicles
          vehiclesList = [];
        }
      } else {
      }
      
      setVehicles(vehiclesList);
      
      // Sync mileage from linked devices
      await syncMileageFromDevices(vehiclesList);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setError('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  }, [sortField, sortDirection, userRole, currentUserId, syncMileageFromDevices]);

  useEffect(() => {
    // Only fetch vehicles once we've determined the user role
    // This prevents fetching all vehicles before we know if user is driver/assistant
    if (roleDetermined) {
      fetchVehicles();
    }
  }, [roleDetermined, fetchVehicles]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check permissions for adding vehicles
      if (!showEditModal && !canAdd) {
        setError('You do not have permission to add vehicles.');
        setLoading(false);
        return;
      }

      // Clean the form data by removing undefined values
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const vehicleData = {
        ...cleanedData,
        createdAt: new Date().toISOString()
      };

      if (showEditModal) {
        await updateDoc(doc(db, 'vehicles', selectedVehicle.id), vehicleData);
        setSuccess('Vehicle updated successfully!');
      } else {
        await addDoc(collection(db, 'vehicles'), vehicleData);
        setSuccess('Vehicle added successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle');
      setError('Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    // Check permissions - only admins and managers can delete
    if (userRole !== 'administrator' && userRole !== 'manager') {
      setError('You do not have permission to delete vehicles.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await deleteDoc(doc(db, 'vehicles', vehicleId));
        setSuccess('Vehicle deleted successfully');
        fetchVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        setError('Failed to delete vehicle');
      }
    }
  };

  const handleEdit = (vehicle) => {
    // Check permissions - only admins and managers can edit
    if (userRole !== 'administrator' && userRole !== 'manager') {
      setError('You do not have permission to edit vehicles.');
      return;
    }
    
    setSelectedVehicle(vehicle);
    setFormData({
      make: vehicle.make,
      model: vehicle.model,
      chassis: vehicle.chassis,
      color: vehicle.color,
      registrationNumber: vehicle.registrationNumber,
      yearOfManufacture: vehicle.yearOfManufacture,
      dateBought: vehicle.dateBought,
      mileage: vehicle.mileage,
      cc: vehicle.cc,
      status: vehicle.status,
      image: vehicle.image,
      logbook: vehicle.logbook,
      insuranceProvider: vehicle.insuranceProvider,
      insurancePolicyNumber: vehicle.insurancePolicyNumber,
      insuranceExpiryDate: vehicle.insuranceExpiryDate,
      insuranceDocument: vehicle.insuranceDocument,
      seatCount: vehicle.seatCount,
      fleetNumber: vehicle.fleetNumber,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      make: '',
      model: '',
      chassis: '',
      color: '',
      registrationNumber: '',
      yearOfManufacture: '',
      dateBought: '',
      mileage: '',
      cc: '',
      status: 'active',
      image: null,
      logbook: null,
      insuranceProvider: '',
      insurancePolicyNumber: '',
      insuranceExpiryDate: '',
      insuranceDocument: null,
      seatCount: '',
      fleetNumber: '',
    });
    setSelectedVehicle(null);
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || vehicle.status === filterStatus;
    
    // Check ignition filter
    let matchesIgnition = true;
    if (filterIgnition !== 'all') {
      const hasDeviceData = vehicle.assignedDevice && 
                           vehicle.assignedDevice.id && 
                           deviceData[vehicle.assignedDevice.id];
      const isIgnitionOn = hasDeviceData && deviceData[vehicle.assignedDevice.id].isIgnitionOn;
      
      if (filterIgnition === 'yes') {
        matchesIgnition = isIgnitionOn === true;
      } else if (filterIgnition === 'no') {
        matchesIgnition = !isIgnitionOn; // true if no device, no data, or ignition is off
      }
    }
    
    // Check device connected filter
    const matchesDeviceConnected = filterDeviceConnected === 'all' ||
      (filterDeviceConnected === 'yes' && vehicle.assignedDevice && vehicle.assignedDevice.id) ||
      (filterDeviceConnected === 'no' && (!vehicle.assignedDevice || !vehicle.assignedDevice.id));
    
    return matchesSearch && matchesStatus && matchesIgnition && matchesDeviceConnected;
  });

  // Pagination logic
  const itemsPerPage = 9;
  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);
  const paginatedVehicles = filteredVehicles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterIgnition, filterDeviceConnected]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingImages(true);
    
    try {
      const file = files[0]; // We'll handle one file at a time
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const response = await fetch('https://novapioneer.andama.ai/api/upload', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              file: event.target.result,
              type: 'vehicles',
              fileName: file.name
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to upload image');
          }

          const data = await response.json();
          if (!data.url) {
            throw new Error('No URL returned from upload');
          }

          setFormData(prev => ({
            ...prev,
            image: data.url
          }));
        } catch (error) {
          console.error('Error uploading image');
          setError('Failed to upload image');
        }
      };

      reader.onerror = () => {
        console.error('Error reading file');
        setError('Failed to read file');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error in image upload');
      setError('Failed to process image');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleLogbookUpload = async (e) => {
    const file = e.target.files[0];
    setUploadingLogbook(true);
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const response = await fetch('https://novapioneer.andama.ai/api/upload', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              file: event.target.result,
              type: 'logbooks',
              fileName: file.name
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to upload logbook');
          }

          const data = await response.json();
          if (!data.url) {
            throw new Error('No URL returned from upload');
          }

          setFormData(prev => ({
            ...prev,
            logbook: data.url
          }));
        } catch (error) {
          console.error('Error uploading logbook');
          setError('Failed to upload logbook');
        }
      };

      reader.onerror = () => {
        console.error('Error reading file');
        setError('Failed to read file');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error in logbook upload');
      setError('Failed to process logbook');
    } finally {
      setUploadingLogbook(false);
    }
  };

  // Fetch bus schedules from optimized routes
  const fetchBusSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const routesRef = collection(db, 'optimizedRoutes');
      const q = query(routesRef, where('status', '==', 'active'));
      const querySnapshot = await getDocs(q);
      
      const schedules = {};
      const busColors = {};
      
      // Generate distinct colors for each bus
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
        '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
      ];
      
      let colorIndex = 0;
      
      // Process each route
      for (const routeDoc of querySnapshot.docs) {
        const route = routeDoc.data();
        // Check if route has required fields
        if (!route.busId) {
          continue; // Skip routes without busId
        }
        if (!route.time) {
          continue; // Skip routes without time
        }
        
        if (!busColors[route.busId]) {
          busColors[route.busId] = colors[colorIndex % colors.length];
          colorIndex++;
        }
        
        if (!schedules[route.busId]) {
          schedules[route.busId] = [];
        }
        
        // Fetch roster data to get date range
        let roster = null;
        if (route.rosterId) {
          try {
            const rosterDoc = await getDoc(doc(db, 'rosters', route.rosterId));
            if (rosterDoc.exists()) {
              roster = rosterDoc.data();
            }
          } catch (error) {
            console.error('Error fetching roster:', error);
          }
        }
        
        // Parse the time and create schedule entries
        const [hours, minutes] = route.time.split(':').map(Number);
        const duration = route.routeMetrics?.totalDurationWithWait || 0;
        const durationMinutes = Math.floor(duration / 60);
        
        const startTime = new Date();
        startTime.setHours(hours, minutes, 0, 0);
        
        const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
        
        // Get roster frequency from saved route data or roster
        let rosterType = 'daily'; // Default
        let rosterFrequency = route.rosterFrequency || roster?.frequency || 'daily';
        let rosterWeekdays = roster?.weekdays || [];
        
        // Helper function to convert weekday names to numbers (1=Monday, 7=Sunday)
        const convertWeekdayNameToNumber = (weekdayName) => {
          const weekdayMap = {
            'Monday': 1,
            'Tuesday': 2,
            'Wednesday': 3,
            'Thursday': 4,
            'Friday': 5,
            'Saturday': 6,
            'Sunday': 7
          };
          return weekdayMap[weekdayName] || null;
        };
        
        // Convert weekday names to numbers if they are strings
        const rosterWeekdaysNumbers = rosterWeekdays.map(day => {
          if (typeof day === 'string') {
            return convertWeekdayNameToNumber(day);
          }
          return day; // Already a number
        }).filter(day => day !== null);
        
        // Helper function to parse date consistently
        const parseDate = (dateValue) => {
          if (!dateValue) return null;
          
          // Firestore Timestamp
          if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            return dateValue.toDate();
          }
          
          // Already a Date object
          if (dateValue instanceof Date) {
            return new Date(dateValue);
          }
          
          // String - handle ISO format
          if (typeof dateValue === 'string') {
            // Extract just the date part (YYYY-MM-DD)
            const datePart = dateValue.split('T')[0];
            // Create date in local timezone to avoid timezone issues
            const [year, month, day] = datePart.split('-').map(Number);
            if (year && month && day) {
              return new Date(year, month - 1, day);
            }
            // Fallback to direct parsing
            return new Date(dateValue);
          }
          
          return null;
        };
        
        // Determine base date from roster or route
        let baseDate = null;
        if (roster && roster.date) {
          baseDate = parseDate(roster.date);
        } else if (route.createdAt) {
          baseDate = parseDate(route.createdAt);
        }
        
        // If no base date available, use today's date
        if (!baseDate || isNaN(baseDate.getTime())) {
          baseDate = new Date();
          baseDate.setHours(0, 0, 0, 0);
        }
        
        // For routes without roster, ensure baseDate is not too far in the past
        // If baseDate is more than 30 days ago, use today
        if (!roster && baseDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          if (baseDate < thirtyDaysAgo) {
            baseDate = new Date(today);
          }
        }
        
        // Normalize to start of day
        baseDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        
        // Get end date from roster
        let endDate = null;
        if (roster && roster.endDate) {
          endDate = parseDate(roster.endDate);
          if (endDate && !isNaN(endDate.getTime())) {
            // Normalize to end of day
            endDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999);
          } else {
            endDate = null;
          }
        }
        
        // For one-time rosters, only add the base date
        if (rosterFrequency === 'once') {
          schedules[route.busId].push({
            id: routeDoc.id,
            type: 'route',
            title: route.name,
            rosterName: route.rosterName,
            tripType: route.tripType,
            rosterType: rosterType,
            rosterFrequency: rosterFrequency,
            startTime: startTime.toTimeString().slice(0, 5),
            endTime: endTime.toTimeString().slice(0, 5),
            duration: route.routeMetrics?.duration || 'N/A',
            color: busColors[route.busId],
            date: new Date(baseDate),
            routeData: route,
            busId: route.busId
          });
        } else {
          // For recurring schedules, generate dates from roster start date to roster end date
          const startDate = new Date(baseDate);
          
          // End date is roster endDate if set, otherwise generate for a reasonable future period
          let maxDate;
          if (endDate && !isNaN(endDate.getTime())) {
            maxDate = new Date(endDate);
          } else {
            // If no end date, show up to 90 days from start date for display
            maxDate = new Date(startDate);
            maxDate.setDate(maxDate.getDate() + 90);
          }
          
          // Generate schedules for each day in the range (from startDate to maxDate)
          const currentDate = new Date(startDate);
          let scheduleIndex = 0;
          
          // Compare dates by date only (ignore time)
          const compareDates = (date1, date2) => {
            const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
            const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
            return d1.getTime() - d2.getTime();
          };
          
          while (compareDates(currentDate, maxDate) <= 0) {
            // If endDate is set, ensure we don't go past it
            if (endDate && compareDates(currentDate, endDate) > 0) {
              break;
            }
            
            const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
            let shouldInclude = false;
            
            if (rosterFrequency === 'daily') {
              shouldInclude = true;
            } else if (rosterFrequency === 'weekdays') {
              // Check if it's a weekday (Monday-Friday)
              if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                // If specific weekdays are set, check if current day is included
                if (rosterWeekdaysNumbers && rosterWeekdaysNumbers.length > 0) {
                  const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to 1-7 format (1=Monday, 7=Sunday)
                  shouldInclude = rosterWeekdaysNumbers.includes(rosterDayOfWeek);
                } else {
                  shouldInclude = true; // All weekdays if no specific days set
                }
              }
            }
            
            if (shouldInclude) {
              const scheduleDate = new Date(currentDate);
              schedules[route.busId].push({
                id: scheduleIndex === 0 ? routeDoc.id : `${routeDoc.id}_${scheduleIndex}`,
                type: 'route',
                title: route.name,
                rosterName: route.rosterName,
                tripType: route.tripType,
                rosterType: rosterType,
                rosterFrequency: rosterFrequency,
                startTime: startTime.toTimeString().slice(0, 5),
                endTime: endTime.toTimeString().slice(0, 5),
                duration: route.routeMetrics?.duration || 'N/A',
                color: busColors[route.busId],
                date: scheduleDate,
                routeData: route,
                isRecurring: scheduleIndex > 0,
                busId: route.busId
              });
              scheduleIndex++;
            }
            
            // Move to next day
            currentDate.setDate(currentDate.getDate() + 1);
          }
        }
      }
      
      setBusSchedules(schedules);
      
      // Also fetch maintenance schedules
      await fetchMaintenanceSchedules();
      
    } catch (error) {
      console.error('Error fetching bus schedules:', error);
      setError('Failed to load bus schedules');
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Fetch maintenance schedules
  const fetchMaintenanceSchedules = async () => {
    try {
      const maintenanceRef = collection(db, 'maintenance');
      const q = query(maintenanceRef, where('status', '==', 'scheduled'));
      const querySnapshot = await getDocs(q);
      
      const maintenance = {};
      
      querySnapshot.docs.forEach(doc => {
        const maintenanceData = doc.data();
        if (maintenanceData.vehicleId) {
          if (!maintenance[maintenanceData.vehicleId]) {
            maintenance[maintenanceData.vehicleId] = [];
          }
          
          const startTime = maintenanceData.time || '09:00';
          let endTime = maintenanceData.endTime;

          if (!endTime) {
            const [hours, minutes] = startTime.split(':').map(Number);
            const startDate = new Date();
            startDate.setHours(hours, minutes, 0, 0);
            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Default to 1 hour
            endTime = endDate.toTimeString().slice(0, 5);
          }

          maintenance[maintenanceData.vehicleId].push({
            id: doc.id,
            type: 'maintenance',
            title: `Maintenance: ${maintenanceData.type}`,
            description: maintenanceData.description,
            scheduledDate: maintenanceData.date ? new Date(maintenanceData.date) : new Date(),
            startTime: startTime,
            endTime: endTime,
            color: '#333333', // Dark gray for maintenance
            maintenanceData: maintenanceData,
            busId: maintenanceData.vehicleId
          });
        }
      });
      
      setMaintenanceSchedules(maintenance);
    } catch (error) {
      console.error('Error fetching maintenance schedules:', error);
    }
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= lastDay || days.length < 42) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  // Get schedules for a specific date
  const getSchedulesForDate = (date) => {
    const dateStr = date.toDateString();
    const schedules = [];
    
    // Add route schedules
    Object.entries(busSchedules).forEach(([busId, busSchedule]) => {
      busSchedule.forEach(schedule => {
        if (schedule.date.toDateString() === dateStr) {
          const bus = vehicles.find(v => v.id === busId);
          schedules.push({
            ...schedule,
            busName: bus ? `${bus.make} ${bus.model} (${bus.registrationNumber})` : 'Unknown Bus',
            fleetNumber: bus?.fleetNumber,
            busId: busId
          });
        }
      });
    });
    
    // Add maintenance schedules
    Object.entries(maintenanceSchedules).forEach(([busId, maintenanceSchedule]) => {
      maintenanceSchedule.forEach(schedule => {
        if (schedule.scheduledDate.toDateString() === dateStr) {
          const bus = vehicles.find(v => v.id === busId);
          schedules.push({
            ...schedule,
            busName: bus ? `${bus.make} ${bus.model} (${bus.registrationNumber})` : 'Unknown Bus',
            fleetNumber: bus?.fleetNumber,
            busId: busId
          });
        }
      });
    });
    
    return schedules;
  };

  // Get schedules for a specific date sorted by time
  const getSchedulesForDateSorted = (date) => {
    const schedules = getSchedulesForDate(date);
    return schedules.sort((a, b) => {
      const timeA = a.startTime || '00:00';
      const timeB = b.startTime || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  // Generate hourly time slots for daily view
  const generateHourlySlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      slots.push({
        hour: hour,
        time: `${hour.toString().padStart(2, '0')}:00`,
        schedules: []
      });
    }
    return slots;
  };

  // Helper to group overlapping events and assign side-by-side positions
  function getSideBySideSchedules(events) {
    if (!events.length) return [];
    
    // Sort events by start time
    const sorted = [...events].sort((a, b) => a.startTimeInMinutes - b.startTimeInMinutes);
    
    // Group overlapping events
    const groups = [];
    let currentGroup = [];
    
    sorted.forEach(event => {
      // Check if this event overlaps with any event in the current group
      const overlapsWithCurrentGroup = currentGroup.some(groupEvent => 
        event.startTimeInMinutes < groupEvent.endTimeInMinutes && 
        event.endTimeInMinutes > groupEvent.startTimeInMinutes
      );
      
      if (overlapsWithCurrentGroup) {
        // Add to current group
        currentGroup.push(event);
      } else {
        // Start a new group
        if (currentGroup.length > 0) {
          groups.push([...currentGroup]);
        }
        currentGroup = [event];
      }
    });
    
    // Add the last group
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }
    
    // Process each group to assign positions
    const result = [];
    groups.forEach(group => {
      // Sort group by start time for consistent positioning
      const sortedGroup = group.sort((a, b) => a.startTimeInMinutes - b.startTimeInMinutes);
      
      // Assign positions within the group
      sortedGroup.forEach((event, index) => {
        const totalEvents = sortedGroup.length;
        const left = (index * 100) / totalEvents;
        const width = 100 / totalEvents;
        
        // Calculate overlap level for visual feedback
        const overlappingEvents = sortedGroup.filter(other => 
          other.id !== event.id &&
          event.startTimeInMinutes < other.endTimeInMinutes && 
          event.endTimeInMinutes > other.startTimeInMinutes
        );
        
        result.push({
          ...event,
          left,
          width,
          overlapCount: overlappingEvents.length,
          totalInGroup: totalEvents,
          groupIndex: index
        });
      });
    });
    
    return result;
  }

  // Get schedules for hourly view
  const getHourlySchedules = (date) => {
    const daySchedules = getSchedulesForDateSorted(date);
    if (!daySchedules.length) return [];
  
    const schedulesWithTime = daySchedules.map(s => {
      if (!s.startTime || !s.endTime) return null;
      const [startHours, startMinutes] = s.startTime.split(':').map(Number);
      const [endHours, endMinutes] = s.endTime.split(':').map(Number);
      return {
        ...s,
        startTimeInMinutes: startHours * 60 + startMinutes,
        endTimeInMinutes: endHours * 60 + endMinutes,
      };
    }).filter(Boolean);
  
    const sideBySideSchedules = getSideBySideSchedules(schedulesWithTime);

    return sideBySideSchedules.map(schedule => {
      const duration = schedule.endTimeInMinutes - schedule.startTimeInMinutes;
      
      // Enhanced conflict detection
      const hasConflict = sideBySideSchedules.some(other =>
        other.id !== schedule.id &&
        other.busId === schedule.busId &&
        (schedule.startTimeInMinutes < other.endTimeInMinutes && schedule.endTimeInMinutes > other.startTimeInMinutes)
      );
      
      // Check for time conflicts (same bus, overlapping times)
      const timeConflict = sideBySideSchedules.some(other =>
        other.id !== schedule.id &&
        other.busId === schedule.busId &&
        (schedule.startTimeInMinutes < other.endTimeInMinutes && schedule.endTimeInMinutes > other.startTimeInMinutes)
      );
      
      // Check for resource conflicts (different buses, same time slot)
      const resourceConflict = sideBySideSchedules.some(other =>
        other.id !== schedule.id &&
        other.busId !== schedule.busId &&
        (schedule.startTimeInMinutes < other.endTimeInMinutes && schedule.endTimeInMinutes > other.startTimeInMinutes)
      );
      
      return {
        ...schedule,
        top: (schedule.startTimeInMinutes / (24 * 60)) * 100,
        height: (duration / (24 * 60)) * 100,
        hasConflict: hasConflict || timeConflict,
        hasTimeConflict: timeConflict,
        hasResourceConflict: resourceConflict,
        duration,
        // Add visual indicators for overlapping events
        isOverlapping: schedule.overlapCount > 0,
        overlapLevel: schedule.overlapCount,
        // Adjust width and left position for better spacing
        adjustedLeft: schedule.left + (schedule.left * 0.02), // Small gap between overlapping events
        adjustedWidth: schedule.width * 0.96, // Slightly smaller width for gaps
      };
    });
  };


  // Helper to group overlapping events by time slot
  function groupOverlappingEvents(schedules) {
    const groups = {};
    schedules.forEach(schedule => {
      const key = `${schedule.startTimeInMinutes}-${schedule.endTimeInMinutes}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(schedule);
    });
    return Object.entries(groups).map(([key, group]) => ({ key, schedules: group }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Vehicles...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your fleet data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Fleet Management</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setShowSchedulesModal(true);
              fetchBusSchedules();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <FaCalendar /> Schedules
          </button>
          {canAdd && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-[#00E0E0] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#00B8B8] transition-colors"
            >
              <FaPlus /> Add Vehicle
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Hide search/filter controls for drivers/assistants with no assigned buses */}
      {!((userRole === 'driver' || userRole === 'assistant') && vehicles.length === 0) && (
        <div className="mb-6 flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          <div className="w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="maintenance">In Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="w-48">
            <select
              value={filterIgnition}
              onChange={(e) => setFilterIgnition(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Ignition</option>
              <option value="yes">ON</option>
              <option value="no">OFF</option>
            </select>
          </div>
          <div className="w-48">
            <select
              value={filterDeviceConnected}
              onChange={(e) => setFilterDeviceConnected(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Devices</option>
              <option value="yes">Device Connected</option>
              <option value="no">No Device</option>
            </select>
          </div>
        </div>
      )}

      {/* Show message for drivers/assistants with no assigned buses */}
      {(userRole === 'driver' || userRole === 'assistant') && vehicles.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FaBus className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">You have no assigned buses</h3>
          <p className="text-gray-500">
            Please contact your manager to get assigned to a bus.
          </p>
        </div>
      ) : (userRole !== 'driver' && userRole !== 'assistant') && filteredVehicles.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FaBus className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No vehicles found</h3>
          <p className="text-gray-500">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedVehicles.map((vehicle) => (
                  <div key={vehicle.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 flex items-stretch overflow-hidden relative">
            {/* Right: Action Icons and Vehicle Image */}
            <div className="flex flex-col items-end w-1/3 min-w-[120px] max-w-[180px] absolute top-0 right-0 h-full p-4 pt-3 z-10">
              {/* Action Icons */}
              <div className="flex gap-2 mb-2">
                {/* Assign Driver - Only for admins and managers */}
                {(userRole === 'administrator' || userRole === 'manager') && (
                  <button
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setShowDriverModal(true);
                    }}
                    className="text-blue-500 hover:text-blue-700"
                    title="Assign Driver"
                  >
                    <FaUserFriends />
                  </button>
                )}
                {/* Assign Device - Only for admins */}
                {userRole === 'administrator' && canAssignDevice && (
                  <button
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setShowDeviceModal(true);
                    }}
                    className="text-[#00E0E0] hover:text-[#00B8B8]"
                    title="Link Device"
                  >
                    <FaNetworkWired />
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedVehicle(vehicle);
                    setShowMaintenanceModal(true);
                  }}
                  className="text-green-500 hover:text-green-700"
                  title="Maintenance"
                >
                  <FaTools />
                </button>
                {/* Edit - Only for admins and managers */}
                {(userRole === 'administrator' || userRole === 'manager') && (
                  <button
                    onClick={() => handleEdit(vehicle)}
                    className="text-yellow-500 hover:text-yellow-700"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                )}
                {/* Delete - Only for admins and managers */}
                {(userRole === 'administrator' || userRole === 'manager') && (
                  <button
                    onClick={() => handleDelete(vehicle.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
              {/* Vehicle Image */}
              {vehicle.image && (
                <div className={`w-full flex flex-col items-end transition-opacity duration-300 ${showDriverModal || showDeviceModal || showEditModal || showMaintenanceModal || showAddModal ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.make} ${vehicle.model}`}
                    className="rounded-lg border border-gray-200 shadow-sm bg-white object-contain w-full max-w-[120px] h-auto"
                    style={{ aspectRatio: '4/3' }}
                  />
               {vehicle.fleetNumber && (
                 <div className="mt-2 flex justify-center items-center">
                   <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                     Fleet #{vehicle.fleetNumber}
                   </span>
                 </div>
               )}
            </div>
          )}
        </div>
            {/* Left: Info */}
            <div className="flex-1 flex flex-col justify-between pr-[calc(33%+1.5rem)]"> {/* padding right to make space for image/controls */}
              <div>
                <div className="flex flex-col gap-1 mb-4">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <FaBus className="text-blue-500" />
                    {vehicle.registrationNumber}
                  </h3>
                  <p className="text-gray-600 font-medium">{vehicle.make} {vehicle.model}</p>
                </div>

                {/* Assigned Personnel */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-100 w-full overflow-hidden">
                  <h4 className="font-semibold text-gray-700 mb-3">Assigned Personnel</h4>
                  <div className="flex flex-col gap-2">
                    {/* Driver */}
                    <div className="flex items-center gap-3">
                      {vehicle.assignedDriver && userProfiles[vehicle.assignedDriver.id] && userProfiles[vehicle.assignedDriver.id].profilePicture ? (
                        <img
                          src={userProfiles[vehicle.assignedDriver.id].profilePicture}
                          alt={vehicle.assignedDriver.name}
                          className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <span className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full bg-gray-200 text-gray-500 text-lg font-bold">
                          {vehicle.assignedDriver && vehicle.assignedDriver.name ? vehicle.assignedDriver.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) : 'D'}
                        </span>
                      )}
                      <div>
                        <span className="block text-sm font-semibold text-gray-700">Driver</span>
                        {vehicle.assignedDriver ? (
                          <span className="block text-sm text-gray-700 font-medium">{vehicle.assignedDriver.name}</span>
                        ) : (
                          <span className="block text-sm text-gray-400 italic">Not assigned</span>
                        )}
                        {vehicle.assignedDriver && (
                          <span className="block text-xs text-gray-500">
                            Phone: {vehicle.assignedDriver.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Assistant */}
                    <div className="flex items-center gap-3">
                      {vehicle.assignedAssistant && userProfiles[vehicle.assignedAssistant.id] && userProfiles[vehicle.assignedAssistant.id].profilePicture ? (
                        <img
                          src={userProfiles[vehicle.assignedAssistant.id].profilePicture}
                          alt={vehicle.assignedAssistant.name}
                          className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <span className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full bg-gray-200 text-gray-500 text-lg font-bold">
                          {vehicle.assignedAssistant && vehicle.assignedAssistant.name ? vehicle.assignedAssistant.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) : 'A'}
                        </span>
                      )}
                      <div>
                        <span className="block text-sm font-semibold text-gray-700">Assistant</span>
                        {vehicle.assignedAssistant ? (
                          <span className="block text-sm text-gray-700 font-medium">{vehicle.assignedAssistant.name}</span>
                        ) : (
                          <span className="block text-sm text-gray-400 italic">Not assigned</span>
                        )}
                        {vehicle.assignedAssistant && (
                          <span className="block text-xs text-gray-500">
                            Phone: {vehicle.assignedAssistant.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Linked Device - Separate section matching Assigned Personnel style */}
                {vehicle.assignedDevice && (
                  <div className="mt-4 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-100 w-full">
                    <div className="flex items-center gap-3">
                      <FaNetworkWired className="text-[#00E0E0] text-lg" />
                      <div className="flex-1">
                        <span className="block text-sm font-semibold text-[#00E0E0] mb-1">Linked Device</span>
                        <span className="block text-sm text-gray-800 font-medium">{vehicle.assignedDevice.deviceName || 'Unknown Device'}</span>
                        <span className="block text-xs text-gray-600 mt-0.5">IMEI: {vehicle.assignedDevice.imeiNumber || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <FaIdCard className="text-gray-400" />
                    Chassis: {vehicle.chassis}
                  </p>
                  <p className="flex items-center gap-2">
                    <FaCalendarAlt className="text-gray-400" />
                    Year: {vehicle.yearOfManufacture}
                  </p>
                  <p className="flex items-center gap-2">
                    <FaTachometerAlt className="text-gray-400" />
                    Mileage: {vehicle.mileage} km
                  </p>
                  <p className="flex items-center gap-2">
                    <FaCogs className="text-gray-400" />
                    CC: {vehicle.cc}
                  </p>
                  <p className="flex items-center gap-2">
                    <FaCalendarCheck className="text-gray-400" />
                    Bought: {new Date(vehicle.dateBought).toLocaleDateString()}
                  </p>
                  <p className="flex items-center gap-2">
                    <FaIdCard className="text-gray-400" />
                    Insurance: {vehicle.insuranceProvider || 'N/A'} ({vehicle.insurancePolicyNumber || 'N/A'})
                  </p>
                  <p className="flex items-center gap-2">
                    <FaCalendarCheck className="text-gray-400" />
                    Insurance Expiry: {vehicle.insuranceExpiryDate ? new Date(vehicle.insuranceExpiryDate).toLocaleDateString() : 'N/A'}
                    {vehicle.insuranceExpiryDate && new Date(vehicle.insuranceExpiryDate) < new Date() && (
                      <span className="ml-2 text-red-600 font-semibold">Expired</span>
                    )}
                    {vehicle.insuranceExpiryDate && new Date(vehicle.insuranceExpiryDate) >= new Date() &&
                      new Date(vehicle.insuranceExpiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                        <span className="ml-2 text-yellow-600 font-semibold">Expiring Soon</span>
                      )}
                  </p>
                  {vehicle.insuranceDocument && (
                    <a
                      href={vehicle.insuranceDocument}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      View Insurance Document
                    </a>
                  )}
                  {vehicle.logbook && (
                    <a
                      href={vehicle.logbook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline block"
                    >
                      View Logbook
                    </a>
                  )}
                  <p className="flex items-center gap-2">
                    <FaUserFriends className="text-gray-400" />
                    Seats: {vehicle.seatCount || 'N/A'}
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    vehicle.status === 'active' ? 'bg-green-100 text-green-800' :
                    vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                  </span>
                </div>
                {/* Speed indicator, ignition and location - bottom right of card */}
                {vehicle.assignedDevice && deviceData[vehicle.assignedDevice.id] && (
                  <div className="absolute bottom-4 right-4 flex flex-col items-end gap-2">
                    <div className="w-16 h-16 rounded-full bg-gray-50 border border-gray-100 flex flex-col items-center justify-center">
                      <span className="text-lg font-bold text-[#00E0E0]">
                        {deviceData[vehicle.assignedDevice.id].vehicleSpeed || 0}
                      </span>
                      <span className="text-xs font-medium text-gray-600">km/h</span>
                    </div>
                    {/* Ignition Status */}
                    <div className="text-right">
                      <span className="text-xs font-medium text-gray-700">
                        Ignition: <span className={deviceData[vehicle.assignedDevice.id].isIgnitionOn ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                          {deviceData[vehicle.assignedDevice.id].isIgnitionOn ? 'ON' : 'OFF'}
                        </span>
                      </span>
                    </div>
                    {/* Location */}
                    {deviceData[vehicle.assignedDevice.id].location && (
                      <div className="text-right max-w-[140px]">
                        <span className="text-[10px] text-gray-500 leading-tight">
                          {deviceData[vehicle.assignedDevice.id].location}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {filteredVehicles.length > itemsPerPage && (
            <div className="flex justify-end items-center mt-4 space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Vehicle Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-black">
                  {showEditModal ? 'Edit Vehicle' : 'Add New Vehicle'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg mb-6">
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                    <input
                      type="text"
                      required
                      value={formData.make}
                      onChange={(e) => setFormData({...formData, make: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <input
                      type="text"
                      required
                      value={formData.model}
                      onChange={(e) => setFormData({...formData, model: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Chassis Number</label>
                    <input
                      type="text"
                      required
                      value={formData.chassis}
                      onChange={(e) => setFormData({...formData, chassis: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                    <input
                      type="text"
                      required
                      value={formData.color}
                      onChange={(e) => setFormData({...formData, color: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Registration Number</label>
                    <input
                      type="text"
                      required
                      value={formData.registrationNumber}
                      onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year of Manufacture</label>
                    <input
                      type="number"
                      required
                      value={formData.yearOfManufacture}
                      onChange={(e) => setFormData({...formData, yearOfManufacture: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date Bought</label>
                    <input
                      type="date"
                      required
                      value={formData.dateBought}
                      onChange={(e) => setFormData({...formData, dateBought: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mileage (km)</label>
                    <input
                      type="number"
                      required
                      value={formData.mileage}
                      onChange={(e) => setFormData({...formData, mileage: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Engine CC</label>
                    <input
                      type="number"
                      required
                      value={formData.cc}
                      onChange={(e) => setFormData({...formData, cc: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">In Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Provider</label>
                    <input
                      type="text"
                      value={formData.insuranceProvider}
                      onChange={e => setFormData({ ...formData, insuranceProvider: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Policy Number</label>
                    <input
                      type="text"
                      value={formData.insurancePolicyNumber}
                      onChange={e => setFormData({ ...formData, insurancePolicyNumber: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Expiry Date</label>
                    <input
                      type="date"
                      value={formData.insuranceExpiryDate}
                      onChange={e => setFormData({ ...formData, insuranceExpiryDate: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Document</label>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setUploadingImages(true);
                          try {
                            const reader = new FileReader();
                            
                            reader.onload = async (event) => {
                              try {
                                const response = await fetch('https://novapioneer.andama.ai/api/upload', {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    file: event.target.result,
                                    type: 'insurance',
                                    fileName: file.name
                                  }),
                                });

                                if (!response.ok) {
                                  throw new Error('Failed to upload insurance document');
                                }

                                const data = await response.json();
                                if (!data.url) {
                                  throw new Error('No URL returned from upload');
                                }

                                setFormData(prev => ({
                                  ...prev,
                                  insuranceDocument: data.url
                                }));
                              } catch (error) {
                                console.error('Error uploading insurance document');
                                setError('Failed to upload insurance document');
                              }
                            };

                            reader.onerror = () => {
                              console.error('Error reading file');
                              setError('Failed to read file');
                            };

                            reader.readAsDataURL(file);
                          } catch (error) {
                            console.error('Error in insurance document upload');
                            setError('Failed to process insurance document');
                          } finally {
                            setUploadingImages(false);
                          }
                        }
                      }}
                      className="w-full"
                      disabled={uploadingImages}
                    />
                    {formData.insuranceDocument && (
                      <a
                        href={formData.insuranceDocument}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline mt-1 inline-block"
                      >
                        View Insurance Document
                      </a>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Seats</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.seatCount}
                      onChange={e => setFormData({ ...formData, seatCount: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fleet Number</label>
                    <input
                      type="text"
                      value={formData.fleetNumber}
                      onChange={e => setFormData({ ...formData, fleetNumber: e.target.value })}
                      placeholder="Enter fleet number"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full"
                      disabled={uploadingImages}
                    />
                    {uploadingImages && (
                      <p className="text-sm text-gray-500 mt-1">Uploading image...</p>
                    )}
                    {formData.image && (
                      <div className="mt-2">
                        <img 
                          src={formData.image} 
                          alt="Vehicle preview" 
                          className="h-32 w-auto object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logbook</label>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={handleLogbookUpload}
                      className="w-full"
                      disabled={uploadingLogbook}
                    />
                    {uploadingLogbook && (
                      <p className="text-sm text-gray-500 mt-1">Uploading logbook...</p>
                    )}
                    {formData.logbook && (
                      <a
                        href={formData.logbook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline mt-1 inline-block"
                      >
                        View Logbook
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      resetForm();
                    }}
                    className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors`}
                  >
                    {showEditModal ? <FaEdit /> : <FaPlus />}
                    <span>{showEditModal ? 'Update Vehicle' : 'Add Vehicle'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Driver Assignment Modal - Only for admins and managers */}
      {showDriverModal && selectedVehicle && (userRole === 'administrator' || userRole === 'manager') && (
        <DriverAssignmentModal
          vehicle={selectedVehicle}
          onClose={() => {
            setShowDriverModal(false);
            setSelectedVehicle(null);
          }}
          onAssignmentUpdated={fetchVehicles}
        />
      )}

      {/* Device Assignment Modal - Only for admins */}
      {showDeviceModal && selectedVehicle && userRole === 'administrator' && canAssignDevice && (
        <DeviceAssignmentModal
          vehicle={selectedVehicle}
          onClose={() => {
            setShowDeviceModal(false);
            setSelectedVehicle(null);
          }}
          onAssignmentUpdated={fetchVehicles}
        />
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && selectedVehicle && (
        <MaintenanceModal
          vehicle={selectedVehicle}
          onClose={() => {
            setShowMaintenanceModal(false);
            setSelectedVehicle(null);
          }}
          onMaintenanceAdded={() => {
            fetchVehicles();
            fetchMaintenanceSchedules();
          }}
        />
      )}

      {/* Schedules Modal */}
      {showSchedulesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-black">Bus Schedules</h2>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowDailyView(!showDailyView)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      showDailyView 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {showDailyView ? 'Monthly View' : 'Daily View'}
                  </button>
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    ←
                  </button>
                  <span className="text-lg font-semibold">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    →
                  </button>
                  <button
                    onClick={() => setShowSchedulesModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>
              </div>

              {loadingSchedules ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mr-3"></div>
                  <span>Loading schedules...</span>
                </div>
              ) : showDailyView ? (
                // Daily View
                <div>
                  {/* Date Selector */}
                  <div className="flex justify-between items-center mb-6">
                    <button
                      onClick={() => {
                        const prevDate = new Date(selectedDate);
                        prevDate.setDate(prevDate.getDate() - 1);
                        setSelectedDate(prevDate);
                      }}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      ← Previous Day
                    </button>
                    <h3 className="text-xl font-semibold">
                      {selectedDate.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </h3>
                    <button
                      onClick={() => {
                        const nextDate = new Date(selectedDate);
                        nextDate.setDate(nextDate.getDate() + 1);
                        setSelectedDate(nextDate);
                      }}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      Next Day →
                    </button>
                  </div>

                  {/* Schedule Summary and Legend */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Schedule Count */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {getSchedulesForDate(selectedDate).length}
                        </div>
                        <div className="text-sm text-gray-600">Total Events</div>
                      </div>
                      
                      {/* Overlapping Events */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600">
                          {getHourlySchedules(selectedDate).filter(s => s.isOverlapping).length}
                        </div>
                        <div className="text-sm text-gray-600">Overlapping Events</div>
                      </div>
                      
                      {/* Conflicts */}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {getHourlySchedules(selectedDate).filter(s => s.hasConflict).length}
                        </div>
                        <div className="text-sm text-gray-600">Conflicts</div>
                      </div>
                    </div>
                    
                    {/* Visual Legend */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Legend:</h4>
                      <div className="flex flex-wrap gap-3 text-xs items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-600 rounded border-2 border-dashed"></div>
                          <span>Overlapping Events</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-red-600 rounded border-2 border-red-600"></div>
                          <span>Conflicts</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-700 rounded"></div>
                          <span>Maintenance</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-800 rounded"></div>
                          <span>School Trip</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-orange-800 rounded"></div>
                          <span>Home Trip</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-green-800 rounded"></div>
                          <span>Field Trip</span>
                        </div>
                        {/* Fleet colors */}
                        {vehicles.filter(v => v.fleetNumber).map(vehicle => (
                          <div key={vehicle.id} className="flex items-center gap-1 ml-2">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: (busSchedules[vehicle.id]?.[0]?.color || '#CCCCCC') }}></div>
                            <span>Fleet #{vehicle.fleetNumber}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Hourly Schedule */}
                  <div className="relative" style={{ height: '72rem' }}>
                    {/* Grid Lines */}
                    {generateHourlySlots().map((slot) => (
                      <div key={slot.hour} className="flex border-t" style={{ height: '3rem' }}>
                        <div className="w-20 py-1 pr-2 text-right text-xs text-gray-400 border-r">
                          {slot.time}
                        </div>
                        <div className="flex-1"></div>
                      </div>
                    ))}
                    
                    {/* Schedules Container */}
                    <div className="absolute top-0 bottom-0 left-20 right-0">
                      {groupOverlappingEvents(getHourlySchedules(selectedDate)).map(({ key, schedules }) => {
                        const groupLeft = schedules[0].adjustedLeft || 0;
                        const groupTop = schedules[0].top;
                        const groupHeight = schedules[0].height;
                        const groupWidth = schedules.reduce((sum, s) => sum + (s.adjustedWidth || 100), 0);
                        const isPopoverOpen = popoverGroupKey === key;
                        const maxVisible = 2;
                        const hiddenCount = schedules.length - maxVisible;
                        const showPopover = isPopoverOpen && schedules.length > maxVisible;

                        return (
                          <div
                            key={key}
                            className="absolute flex items-stretch"
                            style={{
                              top: `calc(${(groupTop / 100) * 72}rem)`,
                              left: `${groupLeft}%`,
                              height: `calc(${(groupHeight / 100) * 72}rem)`,
                              width: `calc(${groupWidth}% - 8px)`
                            }}
                            onMouseEnter={() => setPopoverGroupKey(key)}
                            onMouseLeave={() => setPopoverGroupKey(null)}
                            onClick={() => setPopoverGroupKey(isPopoverOpen ? null : key)}
                          >
                            <>
                              {schedules.slice(0, maxVisible).map((schedule, idx) => {
                                const duration = schedule.duration;
                                return (
                                  <div
                                    key={schedule.id}
                                    className={`relative rounded-lg ${getTextColorForBg(schedule.color)} ${duration <= 20 ? 'p-1' : 'p-1.5'} flex flex-col overflow-hidden shadow-md border ${schedule.hasConflict ? 'border-red-500' : ''} min-w-[120px]'}`}
                                    style={{
                                      backgroundColor: hexToRgba(schedule.color, 0.85),
                                      border: `${schedule.hasConflict ? '2px' : '1px'} solid ${schedule.hasConflict ? 'red' : hexToRgba(schedule.color, 1)}`,
                                      width: schedules.length === 1 ? 'calc(100% - 8px)' : `calc(${schedule.adjustedWidth || 100}% - 8px)`,
                                      minWidth: '120px',
                                      marginLeft: idx > 0 ? '4px' : 0,
                                      zIndex: 10 + idx,
                                      cursor: 'pointer',
                                      transition: 'box-shadow 0.2s',
                                      flex: '1 1 120px',
                                    }}
                                    title={`${schedule.title} (${schedule.startTime} - ${schedule.endTime})`}
                                  >
                                    <div className="font-bold text-xs truncate flex items-center justify-between">
                                      <span>{schedule.title}</span>
                                    </div>
                                    <div className="mt-auto flex justify-between items-end text-xs">
                                      <span className="font-semibold">{schedule.startTime} - {schedule.endTime}</span>
                                      {schedules.length <= maxVisible && (
                                        <div className="flex gap-1 items-center">
                                          {schedule.type === 'maintenance' ? (
                                            <span className="px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium bg-gray-700">
                                              Maintenance
                                            </span>
                                          ) : (
                                            <span className={`px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium ${
                                              schedule.tripType === 'AM' 
                                                ? 'bg-blue-800' 
                                                : schedule.tripType === 'PM' 
                                                ? 'bg-orange-800'
                                                : 'bg-green-800'
                                            }`}>
                                              {schedule.tripType === 'AM' ? 'School' : schedule.tripType === 'PM' ? 'Home' : 'Field Trip'}
                                            </span>
                                          )}
                                          {schedule.fleetNumber && (
                                            <span className="px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium bg-teal-600">
                                              Fleet #{schedule.fleetNumber}
                                            </span>
                                          )}
                                          {schedule.hasConflict && (
                                            <span className="px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium bg-red-600">
                                              Conflict
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}

                              {hiddenCount > 0 && (
                                <div
                                  className="relative rounded-lg bg-gray-200 flex items-center justify-center min-w-[48px] h-full ml-2 cursor-pointer border border-gray-400"
                                  style={{ zIndex: 10 + maxVisible, flex: '1 1 48px' }}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setPopoverGroupKey(key);
                                  }}
                                  title={`Show ${hiddenCount} more events`}
                                >
                                  <span className="font-bold text-gray-700">+{hiddenCount}</span>
                                  {showPopover && (
                                    <div className="absolute left-0 top-full mt-2 bg-white shadow-xl rounded-lg border p-4 z-50 min-w-[220px] max-w-xs">
                                      <div className="font-semibold mb-2">All Events</div>
                                      <div className="space-y-2">
                                        {schedules.map(schedule => (
                                          <div key={schedule.id} className="flex flex-col border-b last:border-b-0 pb-2 last:pb-0">
                                            <span className="font-bold text-sm">{schedule.title}</span>
                                            <span className="text-xs text-gray-600">{schedule.startTime} - {schedule.endTime}</span>
                                            <span className="text-xs text-gray-500">Fleet #{schedule.fleetNumber} • {schedule.busName}</span>
                                            {schedule.hasConflict && <span className="text-xs text-red-600">Conflict</span>}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Detailed Schedule Breakdown */}
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold mb-3">Schedule Details</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Overlapping Events */}
                      {getHourlySchedules(selectedDate).filter(s => s.isOverlapping).length > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h5 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                            <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                            Overlapping Events
                          </h5>
                          <div className="space-y-2">
                            {(() => {
                              const overlappingGroups = {};
                              getHourlySchedules(selectedDate)
                                .filter(s => s.isOverlapping)
                                .forEach(schedule => {
                                  const groupKey = `${schedule.startTimeInMinutes}-${schedule.endTimeInMinutes}`;
                                  if (!overlappingGroups[groupKey]) {
                                    overlappingGroups[groupKey] = [];
                                  }
                                  overlappingGroups[groupKey].push(schedule);
                                });
                              
                              return Object.entries(overlappingGroups).map(([timeKey, schedules]) => (
                                <div key={timeKey} className="bg-white rounded p-3 border border-yellow-300">
                                  <div className="text-sm font-medium text-yellow-700 mb-2">
                                    {schedules[0].startTime} - {schedules[0].endTime} ({schedules.length} events)
                                  </div>
                                  <div className="space-y-1">
                                    {schedules.map(schedule => (
                                      <div key={schedule.id} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2">
                                          <div 
                                            className="w-3 h-3 rounded"
                                            style={{ backgroundColor: schedule.color }}
                                          ></div>
                                          <span className="font-medium">{schedule.title}</span>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          Fleet #{schedule.fleetNumber} • {schedule.busName}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Conflicts */}
                      {getHourlySchedules(selectedDate).filter(s => s.hasConflict).length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                            <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                            Conflicts Detected
                          </h5>
                          <div className="space-y-2">
                            {getHourlySchedules(selectedDate)
                              .filter(s => s.hasConflict)
                              .map(schedule => (
                                <div key={schedule.id} className="bg-white rounded p-3 border border-red-300">
                                  <div className="text-sm font-medium text-red-700 mb-1">
                                    {schedule.title}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    Fleet #{schedule.fleetNumber} • {schedule.startTime} - {schedule.endTime}
                                  </div>
                                  <div className="text-xs text-red-600 mt-1">
                                    ⚠️ This schedule conflicts with another event for the same bus
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Monthly View
                <>
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1 mb-6">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="p-2 text-center font-semibold bg-gray-100">
                        {day}
                      </div>
                    ))}
                    
                    {generateCalendarDays().map((date, index) => {
                      const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                      const isToday = date.toDateString() === new Date().toDateString();
                      const schedules = getSchedulesForDate(date);
                      
                      return (
                        <div
                          key={index}
                          className={`min-h-[120px] p-2 border ${
                            isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                          } ${isToday ? 'ring-2 ring-blue-500' : ''} cursor-pointer hover:bg-gray-50`}
                          onClick={() => {
                            setSelectedDate(date);
                            setShowDailyView(true);
                          }}
                        >
                          <div className={`text-sm font-medium ${
                            isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                          } ${isToday ? 'text-blue-600' : ''}`}>
                            {date.getDate()}
                          </div>
                          
                          <div className="mt-1 space-y-1">
                            {schedules.slice(0, 3).map((schedule, sIndex) => (
                              <div
                                key={sIndex}
                                className="text-xs p-1 rounded cursor-pointer"
                                style={{
                                  backgroundColor: schedule.color,
                                  color: 'white'
                                }}
                                title={`${schedule.busName} - ${schedule.title} (${schedule.startTime}) - ${schedule.rosterFrequency}`}
                              >
                                <div className="font-medium truncate">{schedule.fleetNumber || 'N/A'}</div>
                                <div className="truncate">{schedule.title}</div>
                                <div className="text-xs opacity-75">{schedule.startTime}</div>
                                {schedule.rosterFrequency && (
                                  <div className="text-xs opacity-75">
                                    {schedule.rosterFrequency === 'weekdays' && 'Weekday'}
                                    {schedule.rosterFrequency === 'daily' && 'Daily'}
                                    {schedule.rosterFrequency === 'once' && 'One-time'}
                                  </div>
                                )}
                              </div>
                            ))}
                            {schedules.length > 3 && (
                              <div className="text-xs text-gray-500 text-center">
                                +{schedules.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bus Legend */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Bus Legend</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {vehicles.map(vehicle => {
                        const busSchedule = busSchedules[vehicle.id] || [];
                        const maintenanceSchedule = maintenanceSchedules[vehicle.id] || [];
                        const hasSchedules = busSchedule.length > 0 || maintenanceSchedule.length > 0;
                        
                        if (!hasSchedules) return null;
                        
                        const color = busSchedule[0]?.color || '#CCCCCC';
                        
                        return (
                          <div key={vehicle.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: color }}
                            ></div>
                            <div className="text-sm">
                              <div className="font-medium">Fleet #{vehicle.fleetNumber}</div>
                              <div className="text-gray-500">{vehicle.registrationNumber}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Schedule Details */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Route Schedules */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Route Schedules</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {Object.entries(busSchedules).map(([busId, schedules]) => {
                          const bus = vehicles.find(v => v.id === busId);
                          if (!bus) return null;
                          
                          return schedules.map(schedule => (
                            <div
                              key={schedule.id}
                              className="p-3 border rounded-lg"
                              style={{ borderLeftColor: schedule.color, borderLeftWidth: '4px' }}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium">{schedule.title}</div>
                                  <div className="text-sm text-gray-600">
                                    {bus.make} {bus.model} (Fleet #{bus.fleetNumber})
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {schedule.startTime} - {schedule.endTime} ({schedule.duration})
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {schedule.date.toLocaleDateString()}
                                  </div>
                                  {schedule.rosterFrequency && (
                                    <div className="text-xs text-gray-500">
                                      Frequency: {schedule.rosterFrequency === 'weekdays' && 'Weekday'}
                                      {schedule.rosterFrequency === 'daily' && 'Daily'}
                                      {schedule.rosterFrequency === 'once' && 'One-time'}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    schedule.tripType === 'AM' 
                                      ? 'bg-blue-100 text-blue-800' 
                                      : schedule.tripType === 'PM' 
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {schedule.tripType === 'AM' ? 'School Trip' : schedule.tripType === 'PM' ? 'Home Trip' : 'Field Trip'}
                                  </span>
                                  {schedule.rosterFrequency && (
                                    <div className="mt-1">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        schedule.rosterFrequency === 'weekdays' 
                                          ? 'bg-purple-100 text-purple-800'
                                          : schedule.rosterFrequency === 'daily'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {schedule.rosterFrequency === 'weekdays' && 'Weekday'}
                                        {schedule.rosterFrequency === 'daily' && 'Daily'}
                                        {schedule.rosterFrequency === 'once' && 'One-time'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ));
                        })}
                      </div>
                    </div>

                    {/* Maintenance Schedules */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Maintenance Schedules</h3>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {Object.entries(maintenanceSchedules).map(([busId, schedules]) => {
                          const bus = vehicles.find(v => v.id === busId);
                          if (!bus) return null;
                          
                          return schedules.map(schedule => (
                            <div
                              key={schedule.id}
                              className="p-3 border rounded-lg"
                              style={{ borderLeftColor: schedule.color, borderLeftWidth: '4px' }}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium">{schedule.title}</div>
                                  <div className="text-sm text-gray-600">
                                    {bus.make} {bus.model} (Fleet #{bus.fleetNumber})
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {schedule.description}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {schedule.scheduledDate.toLocaleDateString()}
                                  </div>
                                </div>
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Maintenance
                                </span>
                              </div>
                            </div>
                          ));
                        })}
                      </div>
                      
                      {/* Add Maintenance Schedule Button */}
                      <div className="mt-4 space-y-2">
                        <select
                          value={selectedVehicle?.id || ''}
                          onChange={(e) => {
                            const vehicle = vehicles.find(v => v.id === e.target.value);
                            setSelectedVehicle(vehicle);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        >
                          <option value="">Select a vehicle for maintenance</option>
                          {vehicles.map(vehicle => (
                            <option key={vehicle.id} value={vehicle.id}>
                              Fleet #{vehicle.fleetNumber} - {vehicle.registrationNumber}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => {
                            if (selectedVehicle) {
                              setShowMaintenanceModal(true);
                            } else {
                              setError('Please select a vehicle first');
                            }
                          }}
                          disabled={!selectedVehicle}
                          className={`w-full px-4 py-2 rounded-lg transition-colors flex items-center justify-center ${
                            selectedVehicle 
                              ? 'bg-red-600 text-white hover:bg-red-700' 
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          <FaTools className="inline mr-2" />
                          Schedule Maintenance
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fleet; 